import { ReactElement } from "react";
import { t } from "@lingui/core/macro";

import { Uuid } from "_/types";

import { OrgWithStats, useOrg, useOrgsWithStats } from "_/data/orgs";
import { OrgAvatar } from "_/components/avatar";
import { Loader } from "_/components/loader";
import { Link, useParams } from "_/components/router";
import { ColumnDef, Table } from "_/components/table";

import { SsoConfigSection, SsoStatus } from "./sso";

import * as S from "./styled";

// Org detail view at `/internal/orgs/:orgId`
export const OrgDetailView = (): ReactElement => {
  const { orgId } = useParams<{ orgId: Uuid }>();
  const { data: org, isLoading } = useOrg(orgId);

  if (isLoading || !org) {
    return (
      <S.TabPage>
        <Loader loading />
      </S.TabPage>
    );
  }

  return (
    <S.TabPage>
      <h1>{org.name}</h1>
      <SsoConfigSection orgId={org.id} />
    </S.TabPage>
  );
};

// Main component to display the organization list at `/internal/orgs`.
export const OrgsView = (): ReactElement => {
  const { data: orgs, isLoading } = useOrgsWithStats();

  const columns: ColumnDef<OrgWithStats>[] = [
    {
      id: "avatar",
      cell: ({ row }) => <OrgAvatar resource={row.original} />,
    },
    {
      accessorKey: "name",
      header: t`common.name`,
      cell: ({ row }) => (
        <Link to={`/internal/orgs/${row.original.id}`}>
          {row.original.name}
        </Link>
      ),
    },
    {
      id: "userCount",
      header: t`common.users`,
      accessorFn: (org) => org.userCount ?? -1,
      cell: ({ row }) => <span>{row.original.userCount ?? "—"}</span>,
    },
    {
      id: "sso",
      header: t`common.sso`,
      cell: ({ row }) => <SsoStatus orgId={row.original.id} />,
    },
  ];

  return (
    <S.TabPage>
      <Table columns={columns} data={orgs} loading={isLoading} />
    </S.TabPage>
  );
};
